import React, { useState, useEffect } from 'react';
import { Form, Input, Select, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { memberAPI, departmentAPI, Department } from '../../services/organizationAPI';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const MemberEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [members, setMembers] = useState<any[]>([]);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                const result = await memberAPI.create(data);
                message.success('成员已创建');
                navigate(`/organization/members/${result.data.id}`);
            } else {
                await memberAPI.update(parseInt(id), data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        fetchReferenceData();
        if (!isNew) {
            fetchMember();
        }
    }, [id]);

    const fetchReferenceData = async () => {
        try {
            const [deptRes, memberRes] = await Promise.all([
                departmentAPI.list(),
                memberAPI.list()
            ]);
            const deptData = deptRes.data as any;
            setDepartments(Array.isArray(deptData) ? deptData : deptData?.results || []);
            const memberData = memberRes.data as any;
            setMembers(Array.isArray(memberData) ? memberData : memberData?.results || []);
        } catch (error) {
            message.error('加载参考数据失败');
        }
    };

    const fetchMember = async () => {
        setLoading(true);
        try {
            const res = await memberAPI.get(parseInt(id!));
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('加载成员信息失败');
            navigate('/organization');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            const result = await memberAPI.create(values);
            message.success('成员已创建');
            navigate(`/organization/members/${result.data.id}`);
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    const currentMemberId = form.getFieldValue('id');

    return (
        <FormPageLayout
            title={isNew ? '新增成员' : '编辑成员'}
            breadcrumbs={[
                { title: '组织管理', path: '/organization' },
                { title: '成员管理' },
                { title: isNew ? '新增' : '编辑' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Form.Item name="id" hidden><Input /></Form.Item>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Form.Item name="username" label="用户名 (登录账号)" rules={[{ required: true, message: '请输入用户名' }]}>
                        <Input disabled={!isNew} placeholder="用于系统登录" />
                    </Form.Item>

                    {isNew && (
                        <Form.Item name="password" label="初始密码" rules={[{ required: true, message: '请输入初始密码' }]}>
                            <Input.Password placeholder="设置初始密码" />
                        </Form.Item>
                    )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Form.Item name="first_name" label="真实姓名">
                        <Input placeholder="员工真实姓名" />
                    </Form.Item>
                    <Form.Item name="email" label="邮箱">
                        <Input placeholder="联系邮箱" />
                    </Form.Item>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Form.Item name="job_title" label="职位">
                        <Input placeholder="如：运营专员" />
                    </Form.Item>
                    <Form.Item name="phone" label="手机号">
                        <Input placeholder="联系电话" />
                    </Form.Item>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Form.Item name="department" label="部门">
                        <Select allowClear placeholder="选择部门">
                            {departments.map((d) => (
                                <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="role" label="角色">
                        <Select>
                            <Select.Option value="admin">管理员</Select.Option>
                            <Select.Option value="member">普通成员</Select.Option>
                        </Select>
                    </Form.Item>
                </div>

                <Form.Item name="supervisor" label="直属上级">
                    <Select allowClear placeholder="选择直属上级" showSearch optionFilterProp="children">
                        {members
                            .filter(m => m.id !== currentMemberId)
                            .map((m) => (
                                <Select.Option key={m.id} value={m.user_id}>
                                    {m.first_name || m.username} {m.job_title ? `(${m.job_title})` : ''}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default MemberEdit;
